import { useEffect, useRef, useState } from "react";
import { useSnapshot } from "valtio";
import { cache, volumeLoader } from "@cornerstonejs/core";

import useViewerParams from "shared/hooks/useViewerParams";
import viewerState, { updateSegmentationVisibility } from "stores/viewer";
import { switchNiftiBase } from "lib/cornerstone/setupCornerstoneViewer";
import createNiftiImageIds from "lib/cornerstone/createNiftiImageIds";

import useMicrobleeds from "./useMicrobleeds";
import useMicrobleedUrls from "./useMicrobleedUrls";
import MicrobleedTable from "./MicrobleedTable";
import MicrobleedLegend from "./MicrobleedLegend";
import {
  showMicrobleedPoints,
  clearMicrobleedPoints,
} from "./microbleedPoints";

function MicrobleedView() {
  const { patient_id, acquisition } = useViewerParams();
  const { niftiBases } = useSnapshot(viewerState);
  const initDone = useRef(false);
  const [volReady, setVolReady] = useState(false);

  // The T2* base image for the current session. Drives both the displayed base
  // and (its session) the companion/volume lookup, so everything stays on the
  // same timepoint regardless of how `acquisition` is formatted.
  const t2starBaseUrl = (niftiBases as any)?.t2starmri ?? null;

  const {
    companionUrl,
    spatialVolumeUrl,
    isLoading: urlsLoading,
    error: urlsError,
  } = useMicrobleedUrls(patient_id, acquisition, t2starBaseUrl);

  // EXACT volumeId for THIS session's spatial volume — used everywhere instead
  // of a loose "segm-cmb_spatial-analysis" substring so multiple timepoints in
  // cache never get confused.
  const spatialVolumeId = spatialVolumeUrl ? `nifti:${spatialVolumeUrl}` : null;

  const {
    microbleeds,
    isLoading: mbLoading,
    error: mbError,
  } = useMicrobleeds(companionUrl, spatialVolumeId);

  // Re-run base switch when the timepoint (base url) changes.
  useEffect(() => {
    initDone.current = false;
  }, [t2starBaseUrl]);

  // 1. Show the T2* base image and clear any labelmap overlay (no filled mask).
  useEffect(() => {
    if (initDone.current) return;
    if (!t2starBaseUrl) return;
    initDone.current = true;

    (async () => {
      viewerState.segmentsPerSeries = viewerState.segmentsPerSeries.map(
        (s) => ({ ...s, selected: false }),
      );
      try {
        await updateSegmentationVisibility();
      } catch (e) {
        console.error("[CMB] clear overlay failed:", e);
      }
      try {
        await switchNiftiBase(t2starBaseUrl);
        viewerState.niftiActiveModality = "t2starmri";
      } catch (e) {
        console.error("[CMB] switchNiftiBase failed:", e);
      }
    })();
  }, [t2starBaseUrl]);

  // 2. Load THIS session's segm-cmb_spatial-analysis volume into cache WITHOUT
  //    displaying it. Needed for voxel->world (dots + focus) and spatialLabel.
  //    Store its id in viewerState so MicrobleedRow's focus uses the right one.
  useEffect(() => {
    if (!spatialVolumeUrl || !spatialVolumeId) {
      setVolReady(false);
      return;
    }
    let aborted = false;

    (async () => {
      try {
        if (!cache.getVolume(spatialVolumeId)) {
          const imageIds = await createNiftiImageIds(spatialVolumeUrl);
          const vol = await volumeLoader.createAndCacheVolume(spatialVolumeId, {
            imageIds,
          });
          await vol.load();
        }
        if (!aborted) {
          (viewerState as any).cmbSpatialVolumeId = spatialVolumeId;
          setVolReady(true);
        }
      } catch (e) {
        console.error("[CMB] load spatial volume failed:", e);
      }
    })();

    return () => {
      aborted = true;
    };
  }, [spatialVolumeUrl, spatialVolumeId]);

  // 3. Once both the volume and the list are ready, draw the colored dots.
  useEffect(() => {
    if (!volReady || !microbleeds.length) return;
    showMicrobleedPoints(microbleeds, spatialVolumeId);
    return () => clearMicrobleedPoints();
  }, [volReady, microbleeds, spatialVolumeId]);

  const isLoading = urlsLoading || mbLoading;
  const error = urlsError || mbError;

  if (isLoading) {
    return <div className="p-4 text-stone-400">Loading…</div>;
  }
  if (error) {
    return (
      <div className="p-4 text-red-400">
        Failed to load microbleeds: {error.message}
      </div>
    );
  }
  if (!companionUrl) {
    return (
      <div className="p-4 text-stone-400">
        No microbleed companion file found for this acquisition.
      </div>
    );
  }
  if (!microbleeds.length) {
    return (
      <div className="p-4 text-stone-400">No microbleed data available.</div>
    );
  }

  return (
    <div className="flex flex-col">
      <MicrobleedLegend />
      <MicrobleedTable microbleeds={microbleeds} />
    </div>
  );
}

export default MicrobleedView;
